"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useState, useEffect } from "react";
import { agencyChangeService } from "../services/agencyChangeService";

const ReactQueryProvider = function ({ children }: React.PropsWithChildren) {
  const [client] = useState(
    new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 0, // Real-time by default - individual queries can override with longer staleTime if needed
          gcTime: 10 * 60 * 1000, // 10 minutes - keep data in cache for background
          retry: 1, // Fast failure for better UX
          retryDelay: 1000, // Quick 1s retry
          refetchOnWindowFocus: true, // Refetch when window regains focus for instant refresh
          refetchOnReconnect: true, // Do refetch when reconnecting
          refetchOnMount: true, // Refetch on mount (data is always stale with staleTime: 0)
          refetchInterval: false, // Disable automatic polling by default
        },
        mutations: {
          retry: 1, // Quick failure for mutations
          retryDelay: 1000, // Quick 1s retry
        },
      },
    })
  );

  // Initialize agency change service
  useEffect(() => {
    agencyChangeService.init(client);

    // Cleanup on unmount
    return () => {
      agencyChangeService.destroy();
    };
  }, [client]);

  // Refetch everything once the MFA gate stops refusing us.
  //
  // Password sign-in creates a session before the 2FA code is entered, so every provider
  // that mounts in that window (notifications, branding, presence, phone numbers, profile)
  // fetches once and 403s. They do not retry on their own, so the dashboard rendered empty
  // and only a hard refresh brought it back. The axios interceptor fires 'mfa-satisfied'
  // exactly once on the blocked -> unblocked edge, which is what makes this safe: reacting
  // to every successful response would invalidate in a loop.
  useEffect(() => {
    const onMfaSatisfied = () => {
      void client.invalidateQueries();
    };
    window.addEventListener("mfa-satisfied", onMfaSatisfied);
    return () => window.removeEventListener("mfa-satisfied", onMfaSatisfied);
  }, [client]);

  return (
    <QueryClientProvider client={client}>
      {children}
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  );
};

export default ReactQueryProvider;
